/**
 * frontend/src/pages/valuation/_SetAsideReasons.tsx
 *
 * POL-B item 1 — on Methodology and rates, a reason box for each approach the
 * preview sets aside (zero weight, or not computed): POL-A's `set_aside` on the
 * recompute response. Pre-filled with the server's default where it has one,
 * editable, and saved with the draft (tuning.set_aside_reasons) like every
 * other signer edit here — no separate save. A pre-fill the draft does not
 * hold yet is saved too, so signing does not refuse a reason the signer saw.
 *
 * Hydrates once, when the draft is in (VAL-DRAFT-OVERWRITE-1); a box the signer
 * cleared stays clear (the default is offered once, never pushed back).
 */
import { useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import type { SetAsidePreview } from '@/types/valuationB3'
import type { DraftExtras, SaveDraftBody, SetAsideApproach } from '@/types/valuationDraft'
import { Field } from './_AdjustLegs'
import { useAutosave } from './_useAutosave'

// Owner-approved (OWNER_APPROVALS.log 2026-10-24 13:20 CT).
export const SET_ASIDE_REASON_LABEL = 'Reason for setting this approach aside'

/** The approach headings the report prints (core/valuation/set_aside.py REPORT_NAMES). */
const APPROACH_NAMES: Record<SetAsideApproach, string> = {
  income: 'Income approach', market: 'Market approach', asset: 'Asset approach',
}

const AUTOSAVE_MS = 800
type Reasons = Partial<Record<SetAsideApproach, string>>

function savedReasons(extras: DraftExtras): Reasons {
  const out: Reasons = {}
  for (const [a, r] of Object.entries(extras.set_aside_reasons ?? {})) {
    if (r != null) out[a as SetAsideApproach] = typeof r === 'string' ? r : r.reason
  }
  return out
}

export function useSetAsideReasons(
  hydratedDraft: boolean,
  draftExtras: DraftExtras,
  saveDraft: (body: SaveDraftBody) => Promise<string | null>,
  canOverride: boolean,
  setAside: SetAsidePreview | undefined,
) {
  const [saved, setSaved] = useState<Reasons | null>(null)
  const [reasons, setReasons] = useState<Reasons>({})
  const [prefilled, setPrefilled] = useState(false)
  useEffect(() => {
    if (saved || !hydratedDraft) return
    const s = savedReasons(draftExtras)
    setSaved(s)
    setReasons(s)
  }, [hydratedDraft, draftExtras, saved])

  // The server's default fills a box only the first time it is offered.
  useEffect(() => {
    if (!saved || !setAside) return
    setReasons(prev => {
      const next = { ...prev }
      for (const a of setAside.required) {
        if (next[a] !== undefined) continue
        next[a] = setAside.defaults[a] ?? ''
        if (next[a]) setPrefilled(true)
      }
      return next
    })
  }, [saved, setAside])

  // The whole map is sent (the server replaces it): a non-blank reason per
  // approach set aside now, or null when none.
  const required = setAside?.required ?? []
  const sent: Reasons = {}
  for (const a of required) {
    const text = (reasons[a] ?? '').trim()
    if (text) sent[a] = text
  }
  const payload = { tuning: { set_aside_reasons: Object.keys(sent).length ? sent : null } }
  const heldKey = JSON.stringify(Object.fromEntries(required.filter(a => saved?.[a]).map(a => [a, saved?.[a]])))
  const save = useAutosave(payload, saveDraft, {
    enabled: canOverride && saved !== null && setAside !== undefined,
    delayMs: AUTOSAVE_MS,
    // A pre-fill the draft does not hold yet is an unsaved change, not a baseline.
    dirty: JSON.stringify(sent) !== heldKey,
  })
  // WALKFIX-B F8 — a pre-fill is saved the moment it is shown, not after the
  // typing delay, so going straight to Sign cannot outrun it. Same save path.
  const { saveNow } = save
  useEffect(() => {
    if (!prefilled) return
    setPrefilled(false)
    saveNow().catch(() => undefined) // the failure is already on screen (save.message)
  }, [prefilled, saveNow])

  return {
    required,
    reasons,
    edit: (a: SetAsideApproach, v: string) => setReasons(prev => ({ ...prev, [a]: v })),
    message: save.status === 'error' ? save.message : null,
  }
}

export function SetAsideReasons({ r }: { r: ReturnType<typeof useSetAsideReasons> }) {
  if (!r.required.length) return null
  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        {r.required.map(a => (
          <fieldset key={a}>
            <legend className="mb-1 text-sm font-medium text-gray-900">{APPROACH_NAMES[a]}</legend>
            <Field id={`set_aside_${a}`} type="text" label={SET_ASIDE_REASON_LABEL}
              value={r.reasons[a] ?? ''} onChange={v => r.edit(a, v)} />
          </fieldset>
        ))}
        {r.message && <p className="text-xs text-red-700">{r.message}</p>}
      </CardContent>
    </Card>
  )
}
